import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import Navbar from "../layout/Navbar";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [formData, setFormData] = useState({
    refid: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false); // Snackbar visibility state
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message state
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Severity for snackbar (success or error)

  const navigate = useNavigate(); // Initialize navigation

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false); // Close snackbar
  };

  const handleLogin = async () => {
    setLoading(true);
    
    setSnackbarMessage(""); // Reset snackbar message
    setSnackbarSeverity("success"); // Reset snackbar severity

    const requestBody = {
      ComID: "EH25",
      ReferralID: formData.refid,
      Password: formData.password, // Use user input
      NotificationToken: "NOT123",
      Source: "W",
      DeviceID: "NEW DEVICE",
    };

    try {
      const response = await fetch(
        "https://testing.esnep.com/magnessa/api/vendor/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

     // Check if the StatusCode indicates a failure (non-200 status codes)
     if (data.StatusCode !== 200) {
      
      setSnackbarMessage(data.Message || "An error occurred.");
      setSnackbarSeverity("error"); // Set severity to "error"
      setOpenSnackbar(true); // Show snackbar
      return;
    }

      console.log("Login Successful:", data);

      sessionStorage.setItem("user", JSON.stringify(data));

      // Retrieve the stored redirect path or go to the dashboard
      const redirectPath = sessionStorage.getItem("redirectAfterLogin") || "/dashboard";
      sessionStorage.removeItem("redirectAfterLogin");

      // Show success message in snackbar
      setSnackbarMessage("Login Successful!");
      setSnackbarSeverity("success"); // Set severity to "success"
      setOpenSnackbar(true); // Show snackbar

      navigate(redirectPath); // Navigate only if successful

    } catch (err) {
     
      setSnackbarMessage(err.message); // Show error message in snackbar
      setSnackbarSeverity("error"); // Set severity to "error"
      setOpenSnackbar(true); // Show snackbar
      console.error("Login Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <>
      <Navbar />
      <Box
        sx={{
          maxWidth: 800,
          margin: "auto",
          padding: 4,
          backgroundColor: "#fff",
          minHeight: "450px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "0 !important",
              height: "350px",
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1695527081782-c4ad3005417e?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Login"
              style={{
                width: "100%",
                height: "100%",
                maxHeight: "100%",
                objectFit: "cover",
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              border: "1px solid #e8e8e8",
              padding: "25px !important",
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                color: "#0f1111",
                fontWeight: "bold",
                marginBottom: "20px",
                fontSize: "1.4rem",
              }}
            >
              Login to Your Account
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Referral ID"
                    name="refid"
                    type="text"
                    value={formData.refid}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    InputLabelProps={{ style: { fontSize: 10 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    InputLabelProps={{ style: { fontSize: 10 } }}
                    InputProps={{
                      sx: { height: "43px" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            size="small"
                            sx={{ color: "lightgray" }}
                          >
                            {showPassword ? (
                              <Visibility fontSize="small" />
                            ) : (
                              <VisibilityOff fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      backgroundColor: "#2FA8EF",
                      color: "#fff",
                      fontWeight: "bold",
                      padding: "5px",
                      marginTop: "10px",
                      fontSize: "12px",
                      "&:hover": {
                        backgroundColor: "#1E7CB7",
                      },
                    }}
                  >
                    {loading ? "Logging in..." : "Login"}
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    align="center"
                    sx={{
                      marginTop: 2,
                      color: "#555",
                      fontSize: "0.6rem",
                    }}
                  >
                    <a
                      href="/forgot-password"
                      style={{
                        color: "#0066FF",
                        textDecoration: "none",
                        fontWeight: "bold",
                        fontSize: "0.6rem",
                      }}
                    >
                      Forgot Password?
                    </a>
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    align="center"
                    sx={{
                      marginTop: 1,
                      color: "#555",
                      fontSize: "0.6rem",
                    }}
                  >
                    Don’t have an account?{" "}
                    <a
                      href="/register"
                      style={{
                        color: "#0066FF",
                        textDecoration: "none",
                        fontWeight: "bold",
                      }}
                    >
                      Create one
                    </a>
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar for messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }} >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
